import { useCallback, useState } from 'react'
import { useParams } from 'react-router'
import api from '../../api'
import PageContent from '../../ui/PageContent'
import Tabs from '../../ui/Tabs'
import { Table, useSearchTableQueryState, useSearchTableState } from '../../components'
import { useRoute } from '../router'
import { useTranslation } from 'react-i18next'
import { Button, Menu, MenuItem, Modal } from '../../ui'
import FormWrapper from '../../ui/form/FormWrapper'
import UploadField from '../../ui/form/UploadField'
import TextInput from '../../ui/form/TextInput'
import { TrashIcon } from '../../ui/icons'
import RuleBuilder from '../lists/rules/RuleBuilder'
import { createWrapperRule, isEventWrapper } from '../lists/rules/RuleHelpers'
import { Rule, SearchResult, User, WrapperRule } from '../../types'

const isRuleComplete = (rule: Rule): boolean => {
    if (!('children' in rule)) {
        const noValueOps = ['is set', 'is not set', 'empty']
        return !!rule.path && (noValueOps.includes(rule.operator) || (rule.value !== '' && rule.value !== undefined))
    }
    if (isEventWrapper(rule)) {
        return !!rule.value && !!rule.frequency
            && (!rule.children?.length || rule.children.every(isRuleComplete))
    }
    const wrapper = rule as WrapperRule
    if (!wrapper.children?.length) return false
    return wrapper.children.every(isRuleComplete)
}

export default function UserTabs() {
    const { projectId = '' } = useParams()
    const { t } = useTranslation()
    const route = useRoute()
    const searchState = useSearchTableQueryState(useCallback(async params => await api.users.search(projectId, params), [projectId]))
    const [isUploadOpen, setIsUploadOpen] = useState(false)
    const [isSaveListOpen, setIsSaveListOpen] = useState(false)
    const [tabIndex, setTabIndex] = useState(0)

    const [rule, setRule] = useState<Rule>(() => createWrapperRule('parent'))

    const ruleActive = isRuleComplete(rule)

    const emptyResults: SearchResult<User> = { results: [], limit: 25, nextCursor: '' }
    const ruleQueryState = useSearchTableState(
        useCallback(async params => {
            if (!ruleActive) return emptyResults
            return await api.users.query(projectId, {
                rule,
                cursor: params.cursor,
                limit: params.limit,
            })
        }, [projectId, rule, ruleActive]),
    )

    const removeUsers = async (file: FileList) => {
        await api.users.deleteImport(projectId, file[0])
        await searchState.reload()
        setIsUploadOpen(false)
    }

    const clearRules = () => {
        setRule(createWrapperRule('parent'))
    }

    const saveAsList = async ({ name }: { name: string }) => {
        const list = await api.lists.create(projectId, {
            name,
            type: 'dynamic',
            rule: rule as WrapperRule,
            is_visible: true,
        })
        setIsSaveListOpen(false)
        route(`lists/${list.id}`)
    }

    const columns = [
        { key: 'full_name', title: t('name') },
        { key: 'external_id', title: t('external_id') },
        { key: 'email', title: t('email') },
        { key: 'phone', title: t('phone') },
        { key: 'locale', title: t('locale') },
        { key: 'created_at', title: t('created_at'), sortable: true },
    ]

    return <PageContent
        title={t('users')}
        actions={
            <Menu size="regular">
                <MenuItem icon={<TrashIcon />} onClick={() => setIsUploadOpen(true)}>
                    {t('delete_users')}
                </MenuItem>
            </Menu>
        }>

        <Tabs
            selectedIndex={tabIndex}
            onChange={setTabIndex}
            tabs={[
                {
                    key: 'search',
                    label: t('search'),
                    children: (
                        <Table
                            {...searchState}
                            columns={columns}
                            onSelectRow={({ id }) => route(`users/${id}`)}
                            enableSearch
                            searchPlaceholder={t('search_users')}
                        />
                    ),
                },
                {
                    key: 'filter',
                    label: t('filter'),
                    children: (
                        <>
                            <div className="rule-builder">
                                <RuleBuilder
                                    rule={rule}
                                    setRule={setRule}
                                    listConditions
                                    headerPrefix={t('rule_users_matching')}
                                    headerActions={ruleActive
                                        ? <div style={{ display: 'flex', gap: 8 }}>
                                            <Button variant="secondary" size="small" onClick={clearRules}>
                                                {t('clear_all')}
                                            </Button>
                                            <Button variant="primary" size="small" onClick={() => setIsSaveListOpen(true)}>
                                                {t('save_as_list')}
                                            </Button>
                                        </div>
                                        : undefined
                                    }
                                />
                            </div>
                            <Table
                                {...ruleQueryState}
                                columns={columns}
                                onSelectRow={({ id }) => route(`users/${id}`)}
                            />
                        </>
                    ),
                },
            ]}
        />

        <Modal
            open={isUploadOpen}
            onClose={() => setIsUploadOpen(false)}
            title={t('delete_users')}>
            <FormWrapper<{ file: FileList }>
                onSubmit={async (form) => await removeUsers(form.file)}
                submitLabel={t('delete')}
            >
                {form => <>
                    <p>{t('delete_users_instructions')}</p>
                    <UploadField form={form} name="file" label={t('file')} required />
                </>}
            </FormWrapper>
        </Modal>

        <Modal
            open={isSaveListOpen}
            onClose={() => setIsSaveListOpen(false)}
            title={t('save_as_list')}>
            <FormWrapper<{ name: string }>
                onSubmit={saveAsList}
                submitLabel={t('save')}
            >
                {form => (
                    <TextInput.Field
                        form={form}
                        name="name"
                        label={t('list_name')}
                        required
                    />
                )}
            </FormWrapper>
        </Modal>
    </PageContent>
}
